import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MainLayout } from '../../../../components/MainLayout';
import { PagesHeader } from '../../../../components/shared/PagesHeader';
import { useTranslation } from 'react-i18next';
import { ROUTES } from './AmendBillingAgreement.config';
import { Formik } from 'formik';
import { addNewBillingAgreementInitialValues } from '../../config/initialValues';
import { addNewBillingAgreementValidationSchema } from '../../config/validation';
import {
  amendBillingAgreementFormConfig,
  termsAndConditionsFieldConfig,
} from './formConfig';
import styles from './AmendBillingAgreement.styles.scss';
import BillingAgreementForm from '../../components/BillingAgreementForm/BillingAgreementForm';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import { Button } from 'primereact/button';
import {
  getSpecialAgreementAmendments,
  getSpecialAgreementInfo,
  submitAmendSpecialAgreement,
} from '../../../../services/apis';
import { useNavigate, useParams } from 'react-router';
import { ACTING_AS } from '../../config/constants';
import AmendBillingAgreementDialog from '../../components/AmendBillingAgreementDialog/AmendBillingAgreementDialog';
import { differenceWith, fromPairs, isEqual, toPairs } from 'lodash';
import useUnsavedChangesGuard from '../../../../hooks/useUnsavedChangesGuard';
import {
  mapSpecialBillingAgreementToFormValues,
  uploadFiles,
} from '../../../../helpers/specialBillingHelpers';

const AmendBillingAgreement = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const formRef = useRef();

  const { userInfo } = useAuthentication();

  const isDirtyFn = useCallback(() => formRef.current?.dirty, [formRef]);
  const { guardNavigation } = useUnsavedChangesGuard(
    isDirtyFn,
    t('special_billing.unsaved_changes_message'),
  );

  const [formInitValues, setFormInitValues] = useState(
    addNewBillingAgreementInitialValues({}),
  );

  const [actingAs, setActingAs] = useState(ACTING_AS.SELLER);
  const [agreementType, setAgreementType] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const onSubmit = (values) => {
    let cleanedValues = {
      ...values,
    };

    return submitAmendSpecialAgreement(
      params.id,
      fromPairs(
        differenceWith(
          toPairs(cleanedValues),
          toPairs(formInitValues),
          isEqual,
        ),
      ),
    );
  };

  const checkAmendments = (agreement) => {
    getSpecialAgreementAmendments(params.id).then(({ data }) => {
      if (!data) {
        return;
      }
      const approvedAmendments =
        data?.filter((amendment) => amendment.status === 'Approved') ?? [];
      if (approvedAmendments.length > 0) {
        // Consolidate amendments by keeping non-null values from any amendment
        // Later amendments still take precedence for non-null values
        const consolidatedAmendment = approvedAmendments.reduce(
          (acc, amendment) => {
            return Object.keys(amendment).reduce((result, key) => {
              // Only update if the value is not null or undefined
              if (amendment[key] != null) {
                result[key] = amendment[key];
              }
              if (key === 'agreementFilesDtoList') {
                result[key] = [
                  ...(result[key] || []),
                  ...(amendment[key] || []),
                ];
              }
              return result;
            }, acc);
          },
          approvedAmendments[0],
        );

        const reversedAgreements = mapSpecialBillingAgreementToFormValues(
          consolidatedAmendment,
          agreement,
          agreementType,
        );
        setFormInitValues(reversedAgreements);
      }
    });
  };
  useEffect(() => {
    if (params.id) {
      getSpecialAgreementInfo(params.id)
        .then(({ data }) => {
          const reversedAgreements = mapSpecialBillingAgreementToFormValues(
            data,
            {},
            data.agreementType,
          );
          setAgreementType(data.agreementType);
          setFormInitValues(reversedAgreements);

          if (userInfo.tax_number === data.buyerId) {
            setActingAs(ACTING_AS.BUYER);
          } else if (userInfo.tax_number === data.thirdPartyId) {
            setActingAs(ACTING_AS.THIRD);
          }
          return data;
        })
        .then(checkAmendments)
        .catch(() => {
          navigate('..');
        });
    }
  }, []);

  return (
    <MainLayout className={styles.amendAgreement} eInvoicing={true}>
      <PagesHeader
        withBreadcrumb={true}
        routes={ROUTES({ agreementType, actingAs })}
      />
      <div className={styles.container}>
        <Formik
          innerRef={formRef}
          validateOnMount
          enableReinitialize
          validationSchema={addNewBillingAgreementValidationSchema({
            t,
            actingAs,
            agreementType,
            isAmendment: true,
            initialValues: formInitValues,
          })}
          initialValues={formInitValues}
        >
          {({
            values,
            errors,
            touched,
            isValid,
            dirty,
            setFieldValue,
            setTouched,
            setValues,
            submitForm,
          }) => {
            return (
              <>
                <BillingAgreementForm
                  values={values}
                  errors={errors}
                  isValid={isValid}
                  touched={touched}
                  createAs={actingAs}
                  hideMoreAgreements
                  agreementType={agreementType}
                  submitForm={submitForm}
                  setValues={setValues}
                  formConfig={amendBillingAgreementFormConfig({
                    t,
                    lang: i18n.language,
                    values,
                    setTouched,
                    setFieldValue,
                    actingAs,
                    agreementType,
                  })}
                  termsAndConditionsFieldConfig={termsAndConditionsFieldConfig({
                    t,
                    lang: i18n.language,
                    values,
                    setTouched,
                    setFieldValue,
                    agreementType,
                  })}
                  actionButtons={
                    <>
                      <Button
                        label={t('common.cancel')}
                        outlined
                        size="medium"
                        className={styles.formBtn}
                        type="button"
                        onClick={() => guardNavigation(() => navigate('../'))}
                      />
                      <Button
                        type="button"
                        onClick={() => {
                          setShowSubmitDialog(true);
                        }}
                        size="medium"
                        className={styles.formBtn}
                        label={t('common.submit')}
                        disabled={!values.termsAndConditions}
                      />
                    </>
                  }
                />
                <AmendBillingAgreementDialog
                  isOpen={showSubmitDialog}
                  setIsOpen={setShowSubmitDialog}
                  agreementType={agreementType}
                  values={values}
                  onSubmit={async () => {
                    const isUploadSuccess = await uploadFiles(
                      values,
                      setFieldValue,
                    );
                    if (!isUploadSuccess) {
                      return;
                    }
                    return onSubmit(values);
                  }}
                />
              </>
            );
          }}
        </Formik>
      </div>
    </MainLayout>
  );
};

export default AmendBillingAgreement;
